import { Dispatch, useState, ChangeEvent, SetStateAction } from "react";
import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import { Button, Divider, Grid, InputAdornment, Paper, Slide, Stack, TextField, Typography } from "@mui/material";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

type TChangeRequestPositionedPopperProps = {
	handleSaveRequest: (summary: string, description: string) => void;
	disabled?: boolean;
	open: boolean;
	onOpen: Dispatch<SetStateAction<boolean>>;
	changeRequestButtonClassname?: string;
	label?: string;
};

const ChangeRequestPositionedPopper = (props: TChangeRequestPositionedPopperProps) => {
	const { handleSaveRequest = () => {}, disabled = false, onOpen, open, changeRequestButtonClassname = "", label } = props;
	const outerDiv = document.querySelector("#root");
	const [descValue, setDescValue] = useState("");
	const [summaryValue, setSummaryValue] = useState("");
	const [summaryError, setSummaryError] = useState(false);
	const CHARACTER_LIMIT = 80;

	const handleClick = () => {
		onOpen((prev) => !prev);
	};

	const handleClose = () => {
		onOpen((prev) => !prev);
		setSummaryValue("");
		setDescValue("");
		setSummaryError(false);
	};

	const handleDescChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) => {
		setDescValue(event?.target?.value ?? "");
	};

	const handleSummaryChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) => {
		const newValue = event?.target?.value ?? "";
		setSummaryValue(newValue);
		if (newValue) {
			setSummaryError(false);
		} else {
			setSummaryError(true);
		}
	};

	const handleCrate = () => {
		if (summaryValue) {
			handleSaveRequest(summaryValue, descValue);
			handleClose();
		} else {
			setSummaryError(true);
		}
	};

	return (
		<Box>
			<Button
				id="select-plan-dialog-assign-btn"
				variant="contained"
				disableElevation
				aria-controls="select-plan-dialog-assign-btn-menu"
				className={twMerge(
					clsx("!h-[30px] !rounded-[6px] hover:!shadow-none", {
						"!bg-gray-blue": !(disabled || open),
						"!bg-disabled": disabled || open,
						[changeRequestButtonClassname]: !(disabled || open),
					})
				)}
				disabled={disabled || open}
				onClick={handleClick}
			>
				<Typography className="request-change-btn-text">{label ?? "Request Changes"}</Typography>
			</Button>

			<Popper
				open={open}
				anchorEl={outerDiv}
				disablePortal={false}
				className={open ? "change-request-poper-opened" : "change-request-poper"}
				placement={"bottom-end"}
				transition
				style={{
					zIndex: 1000,
					filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
					position: "fixed",
					bottom: 8,
					right: 15,
					top: "unset",
					left: "unset",
				}}
				modifiers={[
					{
						name: "preventOverflow",
						enabled: true,
						options: {
							altAxis: true,
							altBoundary: true,
							tether: false,
							rootBoundary: "viewport",
							padding: 8,
						},
					},
				]}
			>
				{({ TransitionProps }) => (
					<Slide {...TransitionProps} direction="up" in={open} mountOnEnter unmountOnExit>
						<Paper>
							<Box width={"400px"}>
								<Grid container>
									<Grid item xs={12}>
										<TextField
											id="standard-basic"
											variant="standard"
											placeholder="Summary"
											inputProps={{ maxLength: CHARACTER_LIMIT }}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<Typography className="!font-montserrat text-sm text-gray-400">{`${summaryValue.length}/${CHARACTER_LIMIT}`}</Typography>
													</InputAdornment>
												),
											}}
											className="summary-textfield-washout-review"
											value={summaryValue}
											onChange={handleSummaryChange}
										/>
									</Grid>
									<Grid item xs={12}>
										<Divider color={summaryError ? "red" : undefined} />
									</Grid>

									<Grid item xs={12} sx={{ padding: "0px 15px" }}>
										<TextField
											id="outlined-textarea"
											variant="standard"
											placeholder="Description"
											multiline
											minRows={3}
											maxRows={3}
											fullWidth
											InputProps={{ disableUnderline: true, className: "!font-sans text-sm !text-gray-400" }}
											value={descValue}
											onChange={handleDescChange}
										/>
									</Grid>
									<Grid item xs={12} sx={{ padding: "10px 15px " }}>
										<Stack direction={"row"} justifyContent={"space-between"}>
											<Button
												id="commission-cycle-review-btn"
												variant="contained"
												aria-controls="select-plan-dialog-assign-btn-menu"
												className={"!bg-gray-blue hover:!shadow-none"}
												onClick={handleClose}
											>
												<Typography className="change-request-btn-text">Cancel</Typography>
											</Button>
											<Button
												id="select-plan-dialog-assign-btn"
												variant="contained"
												aria-controls="select-plan-dialog-assign-btn-menu"
												className="!bg-primary hover:!shadow-none h-[30px] rounded-[6px]"
												onClick={handleCrate}
											>
												<Typography className="change-request-btn-text">Create</Typography>
											</Button>
										</Stack>
									</Grid>
								</Grid>
							</Box>
						</Paper>
					</Slide>
				)}
			</Popper>
		</Box>
	);
};

export default ChangeRequestPositionedPopper;
